import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Icon, makeStyles  } from "@material-ui/core";
import { continueAjax } from "./useWorkflowApi";
import WarningModal, { IWarningsInfoModal } from "./WarningModal";
import WorkflowFormControls from "./WorkflowFormControls";
import { useTranslation } from "react-i18next";

interface IButtonConfig {
	text: string;
}

export interface ILoadedConfig {
	title: string;
	width: string | number | false;
	saveButton: IButtonConfig | null,
	cancelButton: IButtonConfig | null
}

const useStyles = makeStyles((theme) => ({
	titleCloseIcon: {
		float: "right",
		color: "#888",
		cursor: "pointer",
	},
	workflowLoadingModal: {
		position: "absolute",
		background: "rgba(255, 255, 255, 0.5)",
		top: "45px",
		bottom: "0px",
		left: "0px",
		right: "0px",
	}
}));

interface IContentDialogHtmlProps {
	html: string;
	onSendForm: () => void;
	isTransient: boolean,
}

function ContentDialogHtml(props: IContentDialogHtmlProps) {
	const divRef = React.useRef<HTMLDivElement>();
	const [reload, setReload] = React.useState(1);
	const qdesk = (window as any).qdesk;
	const $ = (window as any).$;

	React.useEffect(() => {
		$(divRef.current).html(props.html);

		let form = divRef.current.querySelector("form");
		var $form = $(form);
		qdesk.renameFields($form);
		$form.submit((e) => {
			props.onSendForm();
			return false;
		});

		setReload(reload + 1);
		$(divRef.current).trigger('inplaceEditor.doneTemplate');

		qdesk.controls.reload(divRef.current, true);
		_initForm();

	}, [props.html]);

	function _initForm() {
		
		qdesk.wysiwyg.refresh("workflow_NewNoteContent", "Note");
		qdesk.wysiwyg.refresh("workflow_NewCommentContent", "Comment");
		qdesk.wysiwyg.refresh("workflow_ContentHtml", "Content");
		qdesk.wysiwyg.refresh("workflow_NewSolutionContent", "Solution");
		qdesk.wysiwyg.refresh("workflow_TemporarySolution", "Solution");

		qdesk.uploader.printScreen();

		let inputs = $("#ajax-workflow-modal input[type='text']");

		inputs.bind('keypress', (e) => {
			var code = e.keyCode || e.which;
			if (code == 13) { //Enter keycode
				props.onSendForm();
			}
		});
	}

	return (<>
		<div ref={divRef} />
		<WorkflowFormControls
			isTransient={props.isTransient}
			divRef={divRef}
			reload={reload}
		/>
	</>)
};

export default function HtmlWorkflowDialog(props: { html: string, onClose: () => void }) {
	const classes = useStyles();
	const { t } = useTranslation();

	const [configs, setConfigs] = React.useState<ILoadedConfig>(null);
	const [loading, setLoading] = React.useState(false);
	const [errorMessages, setErrorMessages] = React.useState<string[]>(null);
	const [continueUrl, setContinueUrl] = React.useState<string>(null);
	const [isTransient, setIsTransient] = React.useState<boolean>(null);

	const [warningsInfo, setWarningsInfo] = React.useState<IWarningsInfoModal>(null);
	const [redirect, setRedirect] = React.useState<any>(null);

	const [html, setHtml] = React.useState<string>(props.html);

	const content = React.useRef<HTMLDivElement>();

	const $ = (window as any).$;

	React.useEffect(() => {
		(window as any).qdesk.htmlWorkflowDialog = {
			setConfig: handleOnSetConfig,
			setErrors: handleSetError,
			isOpen: () => true,
			getContinueUrl: () => continueUrl,
			setContinueUrl: (url) => setContinueUrl(url),
			setRedirect: (data) => setRedirect(data),
			setIsTransient: (data: boolean) => setIsTransient(data),
		}
	}, []);

	React.useEffect(() => {
		setHtml(props.html);
	}, [props.html]);

	React.useEffect(() => {
		if (!content.current)
			return;
		
	}, [html, content.current]);

	function handleOnSetConfig(settings: ILoadedConfig) {
		setConfigs(settings);
	}

	function handleSetError(errors: string | Array<string>) {
		console.warn(errors);
		if (typeof errors == "string") {
			setErrorMessages([errors]);
			return;
		}
		if (Array.isArray(errors)) {
			setErrorMessages(errors);
			return;
		}
		setErrorMessages(null);
	}

	function _sendForm() {

		_saveChanges(() => {

			if (loading) {
				return;
			}

			var data = {};

			let form = content.current.querySelector("form");
			var $form = $(form);
			$form.serializeArray().map(function (x) {
				if (!data[x.name]) {
					if (x.type == "checkbox" && x.checked)
						data[x.name] = x.value;
					if (x.type != "checkbox") {
						data[x.name] = x.value;
					}
				}
			});
			setLoading(true);
			continueAjax(continueUrl, data,
				(result) => {
					setLoading(false);
					setHtml(result);
				},
				(error) => {
					setLoading(false);
					console.error(error);
				});
		});
	}

	function _saveChanges(callback: () => void) {

		if ((window as any).tinyMCE)
			(window as any).tinyMCE.triggerSave();

		if (("activeElement" in document) && document.activeElement && document.activeElement.classList.contains("ui-autocomplete-input")) {
			// Blur i odczekanie 200ms na operacje przeliczenia w combobox'ach "jQueryUI".
			$(document.activeElement).trigger("blur");
			setTimeout(() => {
				_beforeSend(callback);
			}, 200);
		}
		else {
			_beforeSend(callback);
		}
	}

	function _beforeSend(callback: () => void) {
		let elements = content.current.getElementsByClassName("qd-react-root-control");
		let warnings: string[] = [];

		for (var i = 0; i < elements.length; i++) {
			let element = elements[i] as any;
			if (element.canSubmit) {
				var info = element.canSubmit();
				if (info?.warningMessage)
					warnings.push(info.warningMessage);
			}
		}

		if (warnings.length == 0) {
			callback();
			return;
		}

		setWarningsInfo({
			warnings: warnings,
			callback: callback,
		});
	}

	function getWidth() : ('xs' | 'sm' | 'md' | 'lg' | 'xl' | false) {
		if (
			configs?.width == 'xs'
			|| configs?.width == 'sm'
			|| configs?.width == 'md'
			|| configs?.width == 'lg'
			|| configs?.width == 'xl'
			|| configs?.width === false)
			return configs?.width;

		return 'xl';
	}

	function saveButtonText() {
		return configs?.saveButton?.text ?? t("common.action.save");
	}

	function cancelButtonText() {
		return configs?.cancelButton?.text ?? t("common.action.cancel");
	}

	if (errorMessages && errorMessages.length > 0) {
		return <Dialog
			open={true}
			onClose={(e) => props.onClose()}
		>
			<DialogTitle>{configs?.title}</DialogTitle>
			<DialogContent>
				{errorMessages.map((x, i) => <p key={i} className="text-dager">{x}</p>)}
			</DialogContent>
			<DialogActions>
				<Button onClick={(e) => props.onClose()} color="default" variant="contained">
					{t("common.action.close")}
				</Button>
			</DialogActions>
		</Dialog>
	}
	
	return <>
		{warningsInfo && <WarningModal model={warningsInfo} onClose={() => setWarningsInfo(null)} />}
		<Dialog
			open={true}
			onClose={(e) => props.onClose()}
			fullWidth
			maxWidth={getWidth()}
			disableEnforceFocus
			disableBackdropClick
		>
			<DialogTitle>
				{configs?.title}
				<Icon className={classes.titleCloseIcon} onClick={(e) => props.onClose()}
				>close</Icon>
			</DialogTitle>
			<DialogContent>
				{redirect && <DialogContent><CircularProgress /> Trwa przekierowanie</DialogContent>}
				<div
					id="ajax-workflow-modal"
					className="workflow-dialog"
					data-is-transient={isTransient ? "True" : "False"}
					ref={content}
				>
					<ContentDialogHtml
						isTransient={isTransient}
						html={html}
						onSendForm={() => _sendForm()} />
				</div>
				{loading && <div className={classes.workflowLoadingModal + " workflow-loading-modal"}></div>}
			</DialogContent>
			<DialogActions>
				<Button onClick={(e) => props.onClose()} color="default" variant="contained">
					{cancelButtonText()}
				</Button>
				<Button onClick={(e) => _sendForm()} color="primary" variant="contained" disabled={loading}>
					{saveButtonText()}
				</Button>
			</DialogActions>
		</Dialog>
	</>
}